/* eslint-disable max-lines */
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { StreamableFile } from '@adornis/file-utils/db/streamables.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { XWriter } from '@adornis/writer/x-writer.js';
import { getLogo } from '@ils/examType/logos.js';
import '@ils/osces/client/ils-osce-type.js';
import { hasRole } from '@ils/permissions/hasRole.js';
import { subscribeSlotsForUser } from '@ils/slots/api/slot.js';
import { UserRole } from '@ils/static/enums.js';
import { html, noChange, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { guard } from 'lit/directives/guard.js';
import { ref } from 'lit/directives/ref.js';
import { DateTime, type Duration, type Interval } from 'luxon';
import { combineLatest, firstValueFrom, of, Subject, timer, type Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  sampleTime,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { subscribeAnswerBySlotIDs } from '../../api/answer.js';
import { Answer } from '../../db/answer.js';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { ServerTime } from '../../db/time.js';
import '../components/ils-exam-sidebar.js';
import '../components/ils-exam-topbar.js';
import '../components/ils-start-button.js';
import '../components/video/ils-exam-video-chat.js';
import type { ILSExamVideoChat } from '../components/video/ils-exam-video-chat.js';
import {
  examDaySelector,
  examSlotSelector,
  ExamStateEnum,
  makeInitialExamState,
  type ExamState,
  type NarrowSlot,
} from '../examTypes.js';
import { isSidebarOpen } from '../global-state.js';

@customElement('ils-exam')
export class ILSExam extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          height: '100%',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
        '#main': { flex: '1', boxSizing: 'border-box' },
        '[cell]': {
          height: '100px',
          padding: this.spacing.sm,
        },
        '@keyframes rotateX': {
          from: { transform: 'rotateY(0)' },
          to: { transform: 'rotateY(360deg)' },
        },
      },
    ];
  }

  @state() private readonly examDay = new RXController(
    this,
    routingState.searchParams.pipe(
      distinctUntilChanged((prev, curr) => prev.get('day') === curr.get('day')),
      switchMap(params => {
        const day = params.get('day');
        if (day) return subscribeByID(ExamDay, day)(examDaySelector);
        return of(null);
      }),
      filter(Boolean),
    ),
  );

  @state() private readonly _user = new RXController(this, currentUser<ILSUser>());
  @state() private readonly _slots = new RXController<Observable<NarrowSlot[]>, NarrowSlot[] | undefined>(
    this,
    combineLatest([this.examDay.observable, this._user.observable.pipe(distinctUntilChanged())]).pipe(
      filter(<T, U>(input: [T, U]): input is [NonNullable<T>, NonNullable<U>] => !!input[0] || !!input[1]),
      map(([app, user]) => [app, user, app.slotIDs.length] as const),
      distinctUntilChanged(
        ([prevApp, prevUser, prevSlotIDLength], [currApp, currUser, currSlotIDLength]) =>
          prevApp._id === currApp._id && prevUser._id === currUser._id && prevSlotIDLength === currSlotIDLength,
      ),
      switchMap(
        ([examDay]) =>
          subscribeSlotsForUser(examDay._id)(examSlotSelector).pipe(startWith(examDay.slots)) as Observable<
            NarrowSlot[]
          >,
      ),
      startWith([] as NarrowSlot[]),
    ),
  );

  @state() private readonly _earlyStart = new RXController(this, false);

  @state() private readonly _answers = new RXController(
    this,
    this._slots.observable.pipe(
      filter(Boolean),
      map(slots => [...slots.map(s => s._id)]),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(slotIDs =>
        slotIDs.length
          ? (subscribeAnswerBySlotIDs(slotIDs)(Answer.allFields) as Observable<Answer[]>)
          : of([] as Answer[]),
      ),
      startWith([] as Answer[]),
    ),
  );
  /** start time for calculation of the first interval */
  private readonly enterTime = DateTime.now();

  /** Gameloop like https://stackblitz.com/edit/rxjs-5-based-game-loop-jlhyfx?file=index.ts&devtoolsheight=50 */
  @state() public state = new RXController(
    this,
    combineLatest([ServerTime.subscribeServerTime()(), this._earlyStart.observable.pipe(distinctUntilChanged())]).pipe(
      map(([now]) => DateTime.fromJSDate(now)),
      map(now => this._deriveState(now, this._user.value, this._slots.value)),
    ),
  );

  private getPauseInterval(slot: NarrowSlot, lastSlot?: NarrowSlot) {
    const start = lastSlot?.endTime ?? this.enterTime;
    const end = slot.startTime;
    return start.until(end) as Interval<true>;
  }

  private getSlotInterval(slot: NarrowSlot) {
    const start = slot.startTime;
    const end = slot.endTime ?? slot.startTime.endOf('day');
    return start.until(end) as Interval<true>;
  }

  private getSlotPrepareInterval(slot: NarrowSlot) {
    const start = slot.startTime;
    const end = start.plus({ seconds: slot.osce?.startButtonDuration });
    return start.until(end) as Interval<true>;
  }

  private getSlots(now: DateTime, slots: NarrowSlot[]): { slot?: NarrowSlot; previousSlot?: NarrowSlot; i: number } {
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!slot) throw new Error(`Es existiert kein Slot an diesem Index ${i}.`);
      const interval = this.getSlotInterval(slot);
      // since the slot array is sorted by ascending startTime, we can take the first interval which isAfter or contains now
      if (interval.isAfter(now) || interval.contains(now)) return { previousSlot: slots[i - 1], slot, i };
    }
    return { previousSlot: slots[slots.length - 1], i: slots.length };
  }

  private isOwnSlot(user: ILSUser, slot?: NarrowSlot) {
    if (!slot) return false;
    if (hasRole(user, UserRole.TESTER)) return slot.tester === user._id;
    if (hasRole(user, UserRole.TRAINEE)) return slot.candidateID === user._id;
    return false;
  }

  private calculateProgress(timer: Duration, interval: Interval) {
    return 1 - timer.as('seconds') / interval.toDuration().as('seconds');
  }

  private videoElement?: ILSExamVideoChat;

  private _deriveState(now: DateTime, user: Maybe<ILSUser>, slots?: NarrowSlot[]): ExamState {
    // no user has no access
    if (!user) return makeInitialExamState(ExamStateEnum.NO_ACCESS);

    const examDay = this.examDay.value;
    // no slots means there is no exam day
    if (!slots?.length || !examDay) return makeInitialExamState(ExamStateEnum.NO_DAY);

    // if (hasRole(user, UserRole.TESTER)) now = now.minus({ seconds: 1 });

    const userRole = hasRole(user, UserRole.TRAINEE) ? UserRole.TRAINEE : UserRole.TESTER;
    const mySlots = slots.filter(slot =>
      userRole === UserRole.TRAINEE ? slot.candidateID === user._id : slot.tester === user._id,
    );

    const state: ExamState = makeInitialExamState(ExamStateEnum.INIT, user._id, userRole, mySlots.length);

    let { previousSlot, slot, i } = this.getSlots(now, mySlots);
    if (!slot) slot = this.getSlots(now, slots).slot;
    state.index = i;
    state.slot = slot;
    state.lastSlot = previousSlot;

    const nextSlot = slots
      .sort((s1, s2) => (s1.startTime < s2.startTime ? -1 : s2.startTime < s1.startTime ? 1 : 0))
      .find(s => slot?.endTime && s.startTime > slot.endTime);

    slot = this.isOwnSlot(user, slot) ? slot : undefined;

    const interval = slot && this.getSlotInterval(slot);
    const prepareInterval = slot && this.getSlotPrepareInterval(slot);
    const pauseInterval = slot && this.getPauseInterval(slot, previousSlot);

    // after last slot and after IDate time range
    const lastSlot = slots.at(-1);
    if (!lastSlot) throw new Error('_deriveState no lastSlot');
    const endOfIDate = lastSlot.endTime
      ? examDay.end > lastSlot.endTime
        ? examDay.end.plus({ hours: 1 })
        : lastSlot.endTime.plus({ hours: 1 })
      : examDay.end.plus({ hours: 1 });

    // INIT
    if (previousSlot && !slot && now > endOfIDate) state.state = ExamStateEnum.INIT;
    // PAUSE
    // before first slot but in IDate time range
    // between slots
    else if (slot && pauseInterval /*implied by slot*/ && interval?.isAfter(now)) {
      state.state = ExamStateEnum.PAUSE;

      state.topTimer = interval.start.diff(now);
      state.progress = this.calculateProgress(state.topTimer, pauseInterval);
    }

    // PREPARATION TIME
    // in slot but in prepare time
    else if (slot && interval?.contains(now) && prepareInterval?.contains(now) && !this._earlyStart.value) {
      state.state = ExamStateEnum.PREPARE;

      state.startButtonTimer = prepareInterval.end.diff(now);
      state.startButtonProgress = this.calculateProgress(state.startButtonTimer, prepareInterval);

      state.topTimer = interval.end.diff(now);
      state.progress = this.calculateProgress(state.topTimer, interval);
    }

    // STARTED EARLY
    // in slot but in prepare time but early start was pressed
    else if (slot && interval?.contains(now) && prepareInterval?.contains(now) && this._earlyStart.value) {
      state.state = ExamStateEnum.STARTED_EARLY;

      state.topTimer = interval.end.diff(now);
      state.progress = this.calculateProgress(state.topTimer, interval);
    }

    // RUNNING
    // in slot
    else if (slot && interval?.contains(now) && !(prepareInterval?.contains(now) && !this._earlyStart.value)) {
      state.state = ExamStateEnum.RUNNING;

      if (!nextSlot && !slot.endTime) {
        state.topTimer = examDay.end.diff(now);
        state.progress = this.calculateProgress(state.topTimer, slot.startTime.until(examDay.end) as Interval<true>);
      } else {
        state.topTimer = interval.end.diff(now);
        state.progress = this.calculateProgress(state.topTimer, interval);
      }
    }

    // FINISHED EXAM
    // after last slot
    else if (previousSlot && !slot) {
      state.state = ExamStateEnum.FINISHED;

      state.topTimer = now.diff(now); // set 00:00
      state.index = mySlots.length - 1; // set to last slot
      state.progress = 100; // set progress to 100%
    }

    // ERROR STATE
    else {
      state.state = ExamStateEnum.ERROR;

      logger.info(
        {
          hasSlot: !!slot,
          hasLastSlot: !!previousSlot,
          earlyStart: this._earlyStart.value,
          now,
          slotID: slot?._id,
          slots: this._slots.value?.map(s => s._id),
          lastSlotID: previousSlot?._id,
          examDay: this.examDay.value,
          user: this._user.value,
        },
        'Error in state handling of sam-exam',
      );
    }

    return state;
  }

  private _gameTick(oldState: ExamState, currentState: ExamState) {
    // NOT EARLY START -> EARLY START
    // if trainee and start early was pressed
    if (
      oldState.state !== ExamStateEnum.STARTED_EARLY &&
      currentState.state === ExamStateEnum.STARTED_EARLY &&
      currentState.userRole === UserRole.TRAINEE
    )
      setTimeout(() => this.videoElement?.sendData(JSON.stringify({ type: 'control', control: 'earlyStart' })), 1000);

    // reset local earlyStart state
    if (![ExamStateEnum.PREPARE, ExamStateEnum.STARTED_EARLY, ExamStateEnum.RUNNING].includes(currentState.state))
      this._earlyStart.next(false);

    // reset localText when slot changes
    if (oldState.slot !== currentState.slot) this._localText = '';

    // HANDLE RECORDING

    // RUNNING -> STOPPED EARLY / PAUSE / FINISHED
    // stop recording for previous slot as a trainee if the recorder was recording and no answer was added previously
    /** If the slot was stopped early we need to select the current slot, if not we need to check the last slot */
    const slotToBeChecked = currentState.lastSlot;
    if (
      !!slotToBeChecked &&
      ![ExamStateEnum.PAUSE, ExamStateEnum.FINISHED].includes(oldState.state) &&
      [ExamStateEnum.PAUSE, ExamStateEnum.FINISHED].includes(currentState.state)
    ) {
      if (
        this.videoElement?.recording &&
        currentState.userRole === UserRole.TRAINEE &&
        !this._answers.value?.find(a => a.slotID === slotToBeChecked._id)?.isValid()
      ) {
        this.videoElement.stopRecording(slotToBeChecked);
      } else if (currentState.lastSlot && currentState.lastSlot.osce?.taskType === 'text') {
        const answer = this._answers.value?.find(a => a.slotID === currentState.lastSlot?._id);
        if (answer && this._writer) {
          logger.info('Saving text answer on exam-finish.');
          this.saveAnswerText(slotToBeChecked, this._writer.value.value, true).catch(e =>
            logger.error(e, 'Error while saving final text answer on exam completion'),
          );
        }
      } else {
        logger.error(
          "Recording was not stopped, but probably should've.",
          'recordEnabled:',
          this.videoElement?.recordEnabled,
          'userRole:',
          currentState.userRole,
          'isValid:',
          this._answers.value?.find(a => a.slotID === slotToBeChecked._id)?.isValid(),
        );
      }
    }

    // IN RUNNING AND STARTED_EARLY
    // ensure audio is on in running slot and off in not-running slot
    if (currentState.state === ExamStateEnum.RUNNING || currentState.state === ExamStateEnum.STARTED_EARLY) {
      void this.videoElement?.setMicrophone(true);
    } else {
      void this.videoElement?.setMicrophone(false);
    }

    // IN RUNNING AND STARTED_EARLY
    // start recording as a trainee if slot is running and recording wasn't enabled previously
    if (
      [ExamStateEnum.RUNNING, ExamStateEnum.STARTED_EARLY].includes(currentState.state) &&
      currentState.userRole === UserRole.TRAINEE &&
      !this.videoElement?.recording &&
      currentState.slot?.recordAudio
    )
      this.videoElement?.enableRecording();
  }

  // used to trigger answer save for text exams
  private readonly _saveSubject = new Subject<void>();

  // undefined - not yet saved
  // false - saving
  // true - saved
  @state() private readonly saveState = new RXController<undefined | boolean>(this, undefined);

  override connectedCallback() {
    super.connectedCallback();

    // trigger state change side effects
    this.state.observable
      .pipe(pairwise(), takeUntil(this.disconnected))
      .subscribe(([old, current]) => this._gameTick(old, current));

    this._user.observable
      .pipe(
        filter(Boolean),
        take(1),
        map(user => {
          if (hasRole(user, UserRole.TESTER)) isSidebarOpen.next(false);
        }),
        takeUntil(this.disconnected),
      )
      .subscribe();

    this._saveSubject
      .pipe(sampleTime(3000 + Math.random() * 2000), takeUntil(this.disconnected))
      .subscribe(async () => {
        this.saveState.next(false);
        // wait at least a second here to give the use readable UI
        await Promise.all([this.state.value?.slot?.answer?.save(), firstValueFrom(timer(1000))]);
        this.saveState.next(true);
      });
  }

  // eslint-disable-next-line complexity
  override render() {
    const examState = this.state.value;
    if (!examState) return this.loadingLayout();
    if (!this.examDay.value && !this.examDay.isLoading)
      return html`
        <x-flex center crossaxis-center space="md" ${css({ height: '100%' })}>
          <x-h1> Kein Termin ausgewählt! </x-h1>
        </x-flex>
      `;
    if ([ExamStateEnum.INIT, ExamStateEnum.NO_DAY, ExamStateEnum.NO_ACCESS].includes(examState.state)) {
      return html`
        <x-flex center crossaxis-center space="md" ${css({ height: '100%' })}>
          <x-h1>
            Der ausgewählte Termin ist nur zwischen ${this.examDay.value?.start.toLocaleString(DateTime.DATETIME_SHORT)}
            -
            ${this.examDay.value?.end.toLocaleString(DateTime.DATETIME_SHORT) ??
            this.examDay.value?.start.endOf('day').toLocaleString(DateTime.DATETIME_SHORT)}
            zugänglich.
          </x-h1>
        </x-flex>
      `;
    }
    if (examState.state === ExamStateEnum.ERROR)
      return html`
        <x-flex center crossaxis-center space="md" ${css({ height: '100%' })}>
          <x-h1> Es gab ein Problem beim Anzeigen Ihres Termins. Bitte wenden Sie sich an einen Admin! </x-h1>
        </x-flex>
      `;
    return html`
      ${this.timerDisplay(examState)}
      <x-grid
        id="main"
        rows="50px repeat(6, 1fr) min-content"
        columns="2fr repeat(7, 1fr)"
        ${css({ position: 'relative', minHeight: '0' })}
      >
        ${examState.state === ExamStateEnum.FINISHED
          ? nothing
          : examState.slot?.osce?.taskType === 'voice' || examState.userRole === UserRole.TESTER
          ? this.videoLayout(examState)
          : this.textLayout(examState)}
        ${this.sideBar(examState)}
      </x-grid>
    `;
  }

  private loadingLayout() {
    return html`
      <x-flex
        space="lg"
        ${css({ width: '100%', height: '100%', perspective: '240px', perspectiveOrigin: '50% 45%' })}
        center
        crossaxis-center
      >
        <img src=${getLogo('big')} ${css({ width: '100px', animation: 'rotateX 5s linear 2s infinite' })} />
        <x-h3> Lade Prüfungsdaten... </x-h3>
      </x-flex>
    `;
  }

  private handleData(event: CustomEvent<{ member: string; value: string }>) {
    try {
      if (event.detail.value === 'connected') {
        if (this.state.value?.state !== ExamStateEnum.STARTED_EARLY) return;
        // send current state to members if someone reconnects while in state 'STARTED_EARLY'
        const stateData = { state: this.state.value.state };
        this.videoElement?.sendData(JSON.stringify(stateData), event.detail.member);
        return;
      }

      const message = JSON.parse(event.detail.value);

      // set early state if candidate tells so
      if (message.state === ExamStateEnum.STARTED_EARLY) this._earlyStart.next(true);
      else if (message.type === 'control') {
        if (message.control === 'earlyStart') this._earlyStart.next(true);
      }
    } catch (error: any) {
      // ignore JSON.parse errors
    }
  }

  private _video(state: ExamState) {
    if (!state.slot) return nothing;
    return html`
      <ils-exam-video-chat
        flex
        ${ref(e => (this.videoElement = e as ILSExamVideoChat))}
        @data=${event => this.handleData(event)}
        .currentSlot=${state.slot}
        disableMicrophone
        ?disableCamera=${!state.slot.osce?.enableVideo}
        ${css({
          gridColumn: `3 / -1`,
          gridRow: `1 / -1`,
          padding: this.spacing.lg,
        })}
      >
      </ils-exam-video-chat>
    `;
  }

  private videoLayout(state: ExamState) {
    if (!state.slot) return noChange;
    return html`
      ${this._video(state)}
      ${state.state === ExamStateEnum.RUNNING || state.state === ExamStateEnum.STARTED_EARLY
        ? nothing
        : html`
            <x-flex
              ${css({
                position: 'absolute',
                gridColumn: '3 / -1',
                gridRow: '1 / -1',
                width: '100%',
                height: '100%',
                padding: this.spacing.lg,
              })}
              center
              crossaxis-center
            >
              <x-basic-card padding="md" ${css({ width: '70%', height: '70%' })}>
                <x-flex center crossaxis-center space="md" ${css({ height: '100%', textAlign: 'center' })}>
                  ${state.state === ExamStateEnum.PAUSE
                    ? this.pauseOverlay(state)
                    : state.state === ExamStateEnum.PREPARE
                    ? state.userRole === UserRole.TESTER
                      ? this.waitingForTraineeOverlay(state)
                      : this.canStartOverlay(state)
                    : state.userRole === UserRole.TESTER
                    ? this.traineeStoppedOverlay(state)
                    : this.situationStoppedOverlay(state)}
                </x-flex>
              </x-basic-card>
            </x-flex>
          `}
    `;
  }

  private waitingForTraineeOverlay(state: ExamState) {
    return html`
      <x-h1> Warte auf Prüfling... </x-h1>
      <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        Der Prüfling hat bis zu ${state.slot?.osce?.startButtonDuration ?? 30}sek um sich die Aufgabenstellung
        durchzulesen. Er hat jedoch die Möglichkeit die Situation vor Ablauf dieser Zeit zu starten.
      </x-text>
    `;
  }
  private traineeStoppedOverlay(state: ExamState) {
    return html`
      <x-h1> Situation beendet </x-h1>
      <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        Der Prüfling hat die Situation von seiner Seite aus vorzeitig beendet. Sie sehen Ihr Gegenüber noch bis zum
        geplanten Ende der Situation, sind jedoch wieder stumm geschaltet.
      </x-text>
    `;
  }
  private situationStoppedOverlay(state: ExamState) {
    return html`
      <x-h1> Situation beendet </x-h1>
      <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        Sie haben die Situation vorzeitig beendet. Sie sehen Ihr Gegenüber noch bis zum geplanten Ende dieser Situation,
        sind jedoch wieder stumm geschaltet.
      </x-text>
    `;
  }

  private canStartOverlay(state: ExamState) {
    return html`
      <x-flex horizontal crossaxis-center space="xs">
        <x-icon ${css({ fontSize: '30px' })}> arrow_left </x-icon>
        <x-h1> Lesen Sie jetzt die Informationen zum Fall </x-h1>
      </x-flex>
      <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        Sie werden spätestens in ${state.slot?.osce?.startButtonDuration ?? 270}sek mit ihrem Gesprächspartner
        verbunden.
      </x-text>
      <x-text> Um das Gespräch davor zu beginnen, klicken Sie hier: </x-text>
      <ils-start-button
        .currentTime=${state.startButtonTimer.as('seconds')}
        .progress=${state.startButtonProgress}
        @click=${async () => {
          this._earlyStart.next(true);
        }}
      ></ils-start-button>
    `;
  }

  private pauseOverlay(state: ExamState) {
    return html`
      <x-h1> Pause </x-h1>
      <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}> Warten auf Gesprächsteilnehmende... </x-text>
      <x-text> Sobald Ihr Gegenüber verbunden ist und der Termin startet wird der Ton angeschaltet. </x-text>
      <x-text> Beginnen Sie dann damit, die Aufgabenstellung auf der linken Seite zu lesen. </x-text>
    `;
  }

  // _localText is needed to avoid setting the x-writer value property to an old value (after updating the answer on the server)
  private _localText?: string;
  private _writer?: XWriter;
  private textLayout(state: ExamState) {
    if (!state.slot) return noChange;
    if (state.state === ExamStateEnum.PAUSE)
      return html`
        <x-flex
          ${css({
            position: 'absolute',
            gridColumn: '3 / -1',
            gridRow: '1 / -1',
            width: '100%',
            height: '100%',
            padding: this.spacing.lg,
          })}
          center
          crossaxis-center
        >
          <x-basic-card padding="md" ${css({ width: '70%', height: '70%' })}>
            <x-flex center crossaxis-center space="md" ${css({ height: '100%', textAlign: 'center' })}>
              ${this.pauseOverlay(state)}
            </x-flex>
          </x-basic-card>
        </x-flex>
      `;
    if (this._localText === undefined) this._localText = state.slot.answer?.text;
    return html`
      ${state.slot.osce?.videoFileID
        ? html`
            <x-flex padding="md" space="md" ${css({ gridColumn: '3 / 7', gridRow: '1 / -2' })}>
              <x-text bold> Sie können das Video auch erneut starten oder an bestimmte Stellen springen </x-text>
              <video
                controls
                autoplay
                src=${StreamableFile.getServeLink(state.slot.osce.videoFileID)}
                ${css({ width: '100%', height: '100%' })}
              ></video>
            </x-flex>
          `
        : nothing}
      <x-flex
        padding="md"
        space="md"
        ${css({
          gridColumn: state.slot.osce?.videoFileID ? '7 / -1' : `3 / -1`,
          gridRow: '1 / -2',
          overflowY: 'auto',
          background: state.slot.osce?.videoFileID ? '#d0d0d7ff' : 'transparent',
        })}
      >
        <x-text bold> Arztbrief hier eingeben: </x-text>
        ${guard(
          this._localText,
          () => html`
            <x-writer
              .commands=${[]}
              ${ref(e => {
                this._writer = e as XWriter;
                requestAnimationFrame(() => this._writer?.focus());
              })}
              flex
              placeholder="Hier schreiben..."
              placeholder-mode="hide"
              @value-changed=${async (e: ValueEvent<string>) => {
                await this.saveAnswerText(state.slot, e.detail.value);
                this._saveSubject.next();
              }}
              .value=${this._localText ?? ''}
            ></x-writer>
          `,
        )}
      </x-flex>
      <x-flex
        horizontal
        crossaxis-center
        space-between
        ${css({
          gridColumn: '-3 / -1',
          gridRow: '-2',
          padding: `0 ${this.spacing.md} ${this.spacing.md} ${this.spacing.md}`,
          background: state.slot.osce?.videoFileID ? '#d0d0d7ff' : 'transparent',
        })}
      >
        <x-text tone="subtle">
          ${this.saveState.value === undefined
            ? 'Ihre Antwort wird automatisch gespeichert'
            : this.saveState.value
            ? 'gespeichert'
            : 'speichert...'}
        </x-text>
        <x-button
          mode="outline"
          @click=${async () => {
            if (!this._writer) return;
            await this.saveAnswerText(state.slot, this._writer.value.value, true);
            void XSnackbar.show('Antwort gespeichert!');
          }}
        >
          Antwort speichern
        </x-button>
      </x-flex>
    `;
  }

  private async saveAnswerText(slot: NarrowSlot, text: string, saveImmediately = false) {
    if (!text) return;
    if (!slot.answer) {
      const newAnswer = new Answer({ type: slot.osce?.taskType, text, slotID: slot._id });
      slot.answer = newAnswer as any;
      await newAnswer.create();
    } else {
      slot.answer.text = text;
      if (saveImmediately) await slot.answer.save();
    }
  }

  private timerDisplay(state: ExamState) {
    return html` <ils-exam-topbar .examState=${state}></ils-exam-topbar> `;
  }

  private sideBar(state: ExamState) {
    const gridColumn = `1 / 3`;

    if (state.slot?.isCandidateMissing)
      return html`
        <x-flex
          center
          padding="md"
          ${css({ gridRow: '1 / -1', height: '100%', background: this.colors.neutral.shade(100) })}
        >
          <x-h2> Teilnehmer*in kommt nicht. </x-h2>
        </x-flex>
      `;
    return html`
      <x-flex
        ${css({
          gridRow: '1 / -1',
          gridColumn,
          background: this.colors.neutral.shade(100),
        })}
      >
        <ils-exam-sidebar flex ${css({ minHeight: '0' })} .examState=${state}></ils-exam-sidebar>
      </x-flex>
    `;
  }
}
